import * as React from "react";
import { PropsWithChildren } from "react";

import { noop } from "../../../../../Common/TypingUtils";
import { createHocDisplayName, GetProps } from "../../TypingUtils";
import { normalizeComponentIdExtractor, OptionalComponentIdExtractor } from "../ComponentIdExtraction";

import { ComponentPageObject, useTestPageObjectsRegistry } from "./TestPageObjectsRegistry";

export function useTestPageObject(pageObjectMethodsFactory: () => unknown, deps: React.DependencyList): void {
    const elevateTestPageObjectRef = useElevateTestPageObjectRef();
    React.useEffect(() => {
        elevateTestPageObjectRef(pageObjectMethodsFactory);
    }, deps);
}

type ElevateTestPageObjectRefCallback = (pageObjectMethodsFactory: () => unknown) => void;

const ElevateTestPageObjectRefContext = React.createContext<ElevateTestPageObjectRefCallback>(noop);

function useElevateTestPageObjectRef(): ElevateTestPageObjectRefCallback {
    return React.useContext(ElevateTestPageObjectRefContext);
}

export function withTestPageObjectRegistration<P extends React.ComponentType<GetProps<P>>>(
    WrappedComponent: P,
    inExtractComponentIds?: OptionalComponentIdExtractor<
        Readonly<JSX.LibraryManagedAttributes<P, PropsWithChildren<GetProps<P>>>>
    >
): React.ComponentType<JSX.LibraryManagedAttributes<P, PropsWithChildren<GetProps<P>>>> {
    const extractComponentIds = normalizeComponentIdExtractor(inExtractComponentIds);

    function TestPageObjectRegistrationWrapper(
        props: JSX.IntrinsicAttributes & JSX.LibraryManagedAttributes<P, PropsWithChildren<GetProps<P>>>
    ): JSX.Element {
        const testPageObjectsRegistry = useTestPageObjectsRegistry();
        const pageObjectMethodsRef = React.useRef<ComponentPageObject>({});
        const acceptElevatedTestPageObjectRef = React.useCallback<ElevateTestPageObjectRefCallback>(
            (pageObjectMethodsFactory: () => unknown): void => {
                pageObjectMethodsRef.current = pageObjectMethodsFactory();
            },
            []
        );

        React.useEffect(() => {
            const controlIds = [...extractComponentIds(props)];
            const correlationId = crypto.randomUUID();
            for (const id of controlIds) {
                testPageObjectsRegistry.registerPageObjectHandler(id, correlationId, pageObjectMethodsRef);
            }
            return () => {
                for (const id of controlIds) {
                    testPageObjectsRegistry.unregisterPageObjectHandler(id, correlationId, pageObjectMethodsRef);
                }
            };
        }, [...extractComponentIds(props)]);

        return (
            <ElevateTestPageObjectRefContext.Provider value={acceptElevatedTestPageObjectRef}>
                <WrappedComponent {...props} />
            </ElevateTestPageObjectRefContext.Provider>
        );
    }

    TestPageObjectRegistrationWrapper.displayName = createHocDisplayName(
        WrappedComponent,
        "TestPageObjectRegistration"
    );

    return TestPageObjectRegistrationWrapper;
}
